import React, { useEffect, useState } from 'react';
import { Eye, EyeOff, Pencil, ShieldCheck, CreditCard, Loader2, X, Check, Download, ChevronRight } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import api from '../config/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const accountCardClasses =
  'bg-white rounded-3xl shadow-xl border border-purple-50 p-6 flex-1 min-w-[280px]';

const Account = () => {
  const { user, updateUser } = useAuth();
  const [profile, setProfile] = useState(user || {});
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [errors, setErrors] = useState({});

  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});

  const [subscription, setSubscription] = useState({
    plan: null,
    nextCharge: null,
    features: [],
    status: null,
    hasActiveSubscription: false,
  });
  const [loadingSubscription, setLoadingSubscription] = useState(false);

  const [paymentDetails, setPaymentDetails] = useState({
    method: 'Not added yet',
    status: 'Inactive',
    lastUpdated: null,
  });
  const [installPrompt, setInstallPrompt] = useState(null);
  const [isAppInstalled, setIsAppInstalled] = useState(false);

  useEffect(() => {
    const checkStandaloneMode = () => {
      const iosStandalone = window.navigator.standalone === true;
      const browserStandalone = window.matchMedia('(display-mode: standalone)').matches;
      setIsAppInstalled(iosStandalone || browserStandalone);
    };

    const handleBeforeInstallPrompt = (event) => {
      event.preventDefault();
      setInstallPrompt(event);
      checkStandaloneMode();
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setIsAppInstalled(true);
      toast.success('MyStoryMe app installed successfully!');
    };

    checkStandaloneMode();
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoadingProfile(true);
        const response = await api.get('/users/me');
        if (response.data.success) {
          const userData = response.data.user;
          setProfile(userData);
          setEditForm({
            firstName: userData.firstName || '',
            lastName: userData.lastName || '',
            email: userData.email || '',
          });
        }
      } catch (error) {
        console.error(error);
        toast.error('Failed to load profile');
      } finally {
        setLoadingProfile(false);
      }
    };
    fetchProfile();
  }, []);

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        setLoadingSubscription(true);
        const response = await api.get('/users/subscription-status');
        if (response.data.success) {
          if (response.data.hasActiveSubscription && response.data.planName) {
            const planFeatures = Array.isArray(response.data.plan?.features)
              ? response.data.plan.features
              : typeof response.data.plan?.features === 'string'
                ? (() => { try { return JSON.parse(response.data.plan.features); } catch { return []; } })()
                : [];
            const paymentProvider = response.data.subscription?.payment_provider || 'Stripe';
            const paymentRef = response.data.subscription?.payment_reference;

            setSubscription({
              plan: response.data.planName,
              status: response.data.subscription?.status || (response.data.isCompAccount ? 'comp' : 'active'),
              nextCharge: response.data.subscription?.nextRenewalAt || null,
              features: planFeatures.length
                ? planFeatures
                : ['Stories based on your plan', 'Profiles and themes per plan limits'],
              hasActiveSubscription: true,
            });

            setPaymentDetails({
              method: paymentRef ? `${paymentProvider} ••••` : paymentProvider,
              status: response.data.subscription?.status || (response.data.isCompAccount ? 'comp' : 'Active'),
              lastUpdated: response.data.subscription?.updated_at || null,
              reference: paymentRef || null,
            });
          } else {
            setSubscription({
              plan: null,
              status: null,
              nextCharge: null,
              features: [],
              hasActiveSubscription: false,
            });
            setPaymentDetails({
              method: 'Not added yet',
              status: 'Inactive',
              lastUpdated: null,
              reference: null,
            });
          }
        }
      } catch (error) {
        console.error('Failed to load subscription status:', error);
        // Keep existing fallback subscription state
      } finally {
        setLoadingSubscription(false);
      }
    };

    fetchSubscription();
  }, []);

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return 'Email is required';
    }
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setEditForm({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      email: profile?.email || '',
    });
    setErrors({});
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditForm({
      firstName: profile?.firstName || '',
      lastName: profile?.lastName || '',
      email: profile?.email || '',
    });
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setEditForm((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSaveProfile = async () => {
    // Validate form
    const newErrors = {};
    
    if (!editForm.firstName?.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    const emailError = validateEmail(editForm.email);
    if (emailError) {
      newErrors.email = emailError;
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setSavingProfile(true);
      const response = await api.patch('/users/me', {
        firstName: editForm.firstName.trim(),
        lastName: editForm.lastName.trim() || null,
        email: editForm.email.trim().toLowerCase(),
      });

      if (response.data.success) {
        const updatedUser = response.data.user;
        setProfile(updatedUser);
        updateUser(updatedUser); // Update auth context
        setIsEditing(false);
        toast.success('Profile updated successfully');
        setErrors({});
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update profile';
      toast.error(errorMessage);
      
      // Set specific error for email if it's a duplicate
      if (error.response?.status === 409) {
        setErrors({ email: errorMessage });
      }
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    
    // Clear previous errors
    setPasswordErrors({});
    
    // Validate form
    const newErrors = {};
    
    if (!passwordForm.currentPassword.trim()) {
      newErrors.currentPassword = 'Current password is required';
    }
    
    if (!passwordForm.newPassword.trim()) {
      newErrors.newPassword = 'New password is required';
    } else if (passwordForm.newPassword.length < 6) {
      newErrors.newPassword = 'Password must be at least 6 characters';
    }
    
    if (!passwordForm.confirmPassword.trim()) {
      newErrors.confirmPassword = 'Please confirm your new password';
    } else if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (passwordForm.currentPassword === passwordForm.newPassword) {
      newErrors.newPassword = 'New password must be different from current password';
    }
    
    if (Object.keys(newErrors).length > 0) {
      setPasswordErrors(newErrors);
      return;
    }

    try {
      setSavingPassword(true);
      const response = await api.patch('/users/password', {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword,
      });
      if (response.data.success) {
        toast.success('Password updated successfully');
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setPasswordErrors({});
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to update password';
      toast.error(errorMessage);
      
      // Set specific error for current password if incorrect
      if (error.response?.status === 401) {
        setPasswordErrors({ currentPassword: errorMessage });
      }
    } finally {
      setSavingPassword(false);
    }
  };

  const renderPersonalCard = () => (
    <div className={accountCardClasses}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold text-purple-700">Personal details</h2>
        {!isEditing ? (
          <button
            onClick={handleEditClick}
            className="p-2 hover:bg-purple-50 rounded-lg transition-colors"
            title="Edit profile"
          >
            <Pencil className="h-4 w-4 text-purple-400" />
          </button>
        ) : (
          <div className="flex items-center gap-2">
            <button
              onClick={handleCancelEdit}
              disabled={savingProfile}
              className="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
              title="Cancel"
            >
              <X className="h-4 w-4 text-red-400" />
            </button>
            <button
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="p-2 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
              title="Save changes"
            >
              <Check className="h-4 w-4 text-green-500" />
            </button>
          </div>
        )}
      </div>
      {loadingProfile ? (
        <div className="flex items-center gap-3 text-gray-500">
          <Loader2 className="h-5 w-5 animate-spin" />
          Loading profile...
        </div>
      ) : isEditing ? (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">First Name *</label>
            <input
              type="text"
              value={editForm.firstName}
              onChange={(e) => handleInputChange('firstName', e.target.value)}
              className={`w-full border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                errors.firstName ? 'border-red-300' : 'border-purple-100'
              }`}
              placeholder="Enter first name"
            />
            {errors.firstName && (
              <p className="text-red-500 text-xs mt-1">{errors.firstName}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Last Name</label>
            <input
              type="text"
              value={editForm.lastName}
              onChange={(e) => handleInputChange('lastName', e.target.value)}
              className="w-full border border-purple-100 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              placeholder="Enter last name"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email *</label>
            <input
              type="email"
              value={editForm.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              className={`w-full border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                errors.email ? 'border-red-300' : 'border-purple-100'
              }`}
              placeholder="Enter email address"
            />
            {errors.email && (
              <p className="text-red-500 text-xs mt-1">{errors.email}</p>
            )}
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={handleCancelEdit}
              disabled={savingProfile}
              className="px-6 py-2 border border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition disabled:opacity-60 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSaveProfile}
              disabled={savingProfile}
              className="px-6 py-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-xl shadow-lg transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {savingProfile ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-gray-500">First Name</span>
            <span className="text-gray-900 font-semibold">{profile?.firstName || '—'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Last Name</span>
            <span className="text-gray-900 font-semibold">{profile?.lastName || '—'}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-gray-500">Email</span>
            <span className="text-purple-700 font-semibold break-all">{profile?.email || '—'}</span>
          </div>
        </div>
      )}
    </div>
  );

  const renderPasswordCard = () => (
    <div className={accountCardClasses}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold text-purple-700">Security</h2>
        <ShieldCheck className="h-5 w-5 text-purple-400" />
      </div>
      <form onSubmit={handlePasswordSubmit} className="space-y-4">
        {[
          { field: 'currentPassword', placeholder: 'Current password' },
          { field: 'newPassword', placeholder: 'New password' },
          { field: 'confirmPassword', placeholder: 'Confirm new password' },
        ].map(({ field, placeholder }) => (
          <div key={field}>
            <div className="relative">
              <input
                type={showPasswords[field] ? 'text' : 'password'}
                value={passwordForm[field]}
                onChange={(e) => {
                  setPasswordForm((prev) => ({
                    ...prev,
                    [field]: e.target.value,
                  }));
                  // Clear error when user starts typing
                  if (passwordErrors[field]) {
                    setPasswordErrors((prev) => ({ ...prev, [field]: '' }));
                  }
                }}
                placeholder={placeholder}
                className={`w-full border rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent ${
                  passwordErrors[field] ? 'border-red-300' : 'border-purple-100'
                }`}
              />
              <button
                type="button"
                onClick={() =>
                  setShowPasswords((prev) => ({
                    ...prev,
                    [field]: !prev[field],
                  }))
                }
                className="absolute right-4 top-1/2 -translate-y-1/2 text-purple-400 hover:text-purple-600 transition-colors"
              >
                {showPasswords[field] ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {passwordErrors[field] && (
              <p className="text-red-500 text-xs mt-1">{passwordErrors[field]}</p>
            )}
          </div>
        ))}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={savingPassword}
            className="px-6 py-2 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl shadow-lg transition disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {savingPassword ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      </form>
    </div>
  );

  const renderSubscriptionCard = () => (
    <div className={accountCardClasses}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold text-purple-700">Subscription</h2>
        <Pencil className="h-4 w-4 text-purple-400" />
      </div>
      <p className="text-sm text-gray-500 mb-2">Subscription Plan</p>
      {loadingSubscription ? (
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 animate-spin text-purple-500" />
          <span className="text-sm text-gray-500">Loading...</span>
        </div>
      ) : subscription.hasActiveSubscription && subscription.plan ? (
        <>
          <div className="flex items-center gap-2">
            <p className="text-lg font-semibold text-gray-900">{subscription.plan}</p>
            {subscription.status && (
              <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-50 text-purple-700 capitalize">
                {subscription.status}
              </span>
            )}
          </div>
          {subscription.features.length > 0 && (
            <ul className="mt-4 space-y-2 text-sm text-gray-600">
              {subscription.features.map((feature, index) => (
                <li key={index}>• {feature}</li>
              ))}
            </ul>
          )}
          {subscription.nextCharge ? (
            <p className="text-xs text-gray-400 mt-4">Next charge: {new Date(subscription.nextCharge).toLocaleDateString()}</p>
          ) : (
            <p className="text-xs text-gray-400 mt-4">Upgrade to unlock pro adventures</p>
          )}
        </>
      ) : (
        <>
          <div className="flex items-center gap-2">
            <p className="text-lg font-semibold text-gray-600">No Active plan</p>
          </div>
          <p className="text-xs text-gray-400 mt-4">Upgrade to unlock pro adventures</p>
        </>
      )}
    </div>
  );

  const renderPaymentCard = () => (
    <div className={accountCardClasses}>
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-bold text-purple-700">Payment details</h2>
        <Pencil className="h-4 w-4 text-purple-400" />
      </div>
      <div className="flex items-center gap-3 text-gray-600">
        <CreditCard className="h-5 w-5" />
        <div>
          <p className="text-sm font-semibold text-gray-900">{paymentDetails.method}</p>
          <p className="text-xs text-gray-500">
            Status: <span className="font-medium">{paymentDetails.status}</span>
          </p>
          {paymentDetails.lastUpdated && (
            <p className="text-xs text-gray-400">Updated {paymentDetails.lastUpdated}</p>
          )}
          {paymentDetails.reference && (
            <p className="text-xs text-gray-400">Ref: {paymentDetails.reference}</p>
          )}
        </div>
      </div>
      <button className="mt-6 px-5 py-2 text-sm font-semibold text-purple-600 border border-purple-200 rounded-xl hover:bg-purple-50 transition">
        Add payment method
      </button>
    </div>
  );

  const handleInstallApp = async () => {
    if (isAppInstalled) {
      toast('MyStoryMe is already installed on this device.', { icon: '✅' });
      return;
    }

    if (installPrompt) {
      try {
        installPrompt.prompt();
        const { outcome } = await installPrompt.userChoice;
        if (outcome === 'accepted') {
          toast.success('Installing MyStoryMe app...');
        } else {
          toast('Installation cancelled.', { icon: 'ℹ️' });
        }
      } finally {
        setInstallPrompt(null);
      }
      return;
    }

    toast('Open browser menu and choose "Install app" or "Add to Home Screen".', {
      icon: '📲',
    });
  };

  const renderPwaInstallCard = () => (
    <div className="rounded-2xl border border-[#1a2e60] bg-[#07163a] p-3 sm:p-4 shadow-xl text-white">
      <div className="text-[9px] uppercase tracking-[0.35em] text-blue-200/75 mb-2 px-1">App</div>
      <button
        type="button"
        onClick={handleInstallApp}
        aria-label="Install MyStoryMe app"
        className="w-full text-left rounded-xl border border-blue-300/25 bg-[#142a5c] px-4 py-3 hover:bg-[#1a336d] transition-colors duration-200"
      >
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-blue-400/20 border border-blue-300/20 flex items-center justify-center shrink-0">
            <Download className="h-4 w-4 text-blue-100" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[18px] leading-tight font-semibold text-white">
              {isAppInstalled ? 'App installed on this device' : 'Add to Home Screen'}
            </p>
            <p className="text-[12px] text-blue-100/75 mt-1">
              {isAppInstalled
                ? 'Open MyStoryMe directly from your device.'
                : 'Save the app to your phone - takes 10 seconds'}
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-blue-100/70 shrink-0" />
        </div>
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-white flex flex-col">
      <Header />
      <main className="container mx-auto px-4 py-10 flex-1">
        <div className="text-center mb-10">
          <p className="text-sm uppercase tracking-[0.4em] text-purple-400">Account center</p>
          <h1 className="text-3xl lg:text-4xl font-bold text-gray-900 mt-2">
            Manage your profile and subscription
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto mt-3">
            Keep your details up to date, adjust your password, and review your subscription status in one place.
          </p>
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          {renderPersonalCard()}
          {renderPasswordCard()}
        </div>

        <div className="grid gap-6 lg:grid-cols-2 mt-6">
          {renderSubscriptionCard()}
          {renderPaymentCard()}
        </div>

        <div className="mt-6">
          {renderPwaInstallCard()}
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Account;

